import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartMorphEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * The cached recommendations
   * @type {Record<string, Promise<string>>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartMorph, this.#handleCartMorph);
    this.#loadRecommendations();
    this.#checkCartDrawerCookie();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartMorph, this.#handleCartMorph);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Check if cart_drawer cookie exists and open drawer if found
   */
  #checkCartDrawerCookie() {
    if (document.cookie.indexOf('cart_drawer=') !== -1) {
      this.showDialog();
      this.#removeCartDrawerCookie();
    }
  }

  /**
   * Remove the cart_drawer cookie
   */
  #removeCartDrawerCookie() {
    // Delete at root path (current setter location) and at each parent path
    // segment so we also clean up stale cookies that were previously scoped to
    // a locale prefix like `/en`.
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `cart_drawer=; path=/; ${expiry}`;
    const segments = window.location.pathname.split('/').filter(Boolean);
    let path = '';
    for (const segment of segments) {
      path += `/${segment}`;
      document.cookie = `cart_drawer=; path=${path}; ${expiry}`;
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {CartMorphEvent} event
   */
  #handleCartMorph = async (event) => {
    if (event.target === this) return;

    try {
      await this.#loadRecommendations();
    } catch (error) {
      this.#handleRecommendationsError(error);
    }
  };

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const recommendationsElement = this.querySelector('.drawer__recommendations');
    const mobileRecommendationsElement = this.querySelector('.mobile-drawer__recommendations');
    if (!recommendationsElement || !mobileRecommendationsElement) return;

    const productId = this.productId;
    if (!productId) {
      /** @type {HTMLElement} */ (recommendationsElement).hidden = true;
      /** @type {HTMLElement} */ (mobileRecommendationsElement).hidden = true;
      return;
    }

    const result = await this.#fetchCachedRecommendations(productId);

    if (!result.success) {
      this.#handleRecommendationsError(new Error(`Server returned ${result.status}`));
      return;
    }

    const html = document.createElement('div');
    html.innerHTML = result.data || '';
    const recommendations = html.querySelector('.drawer__recommendations');
    const mobileRecommendations = html.querySelector('.mobile-drawer__recommendations');

    if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
      recommendationsElement.innerHTML = recommendations.innerHTML;
      /** @type {HTMLElement} */ (recommendationsElement).hidden = false;
    } else {
      /** @type {HTMLElement} */ (recommendationsElement).hidden = true;
    }

    if (mobileRecommendations?.innerHTML && mobileRecommendations.innerHTML.trim().length) {
      mobileRecommendationsElement.innerHTML = mobileRecommendations.innerHTML;
      /** @type {HTMLElement} */ (mobileRecommendationsElement).hidden = false;
    } else {
      /** @type {HTMLElement} */ (mobileRecommendationsElement).hidden = true;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId) {
    const url = `${Theme.routes?.product_recommendations_url}?product_id=${productId}&limit=4&section_id=${this.dataset.sectionId}`;

    if (this.#cachedRecommendations[url]) {
      const data = await this.#cachedRecommendations[url];
      return { success: true, data };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const responsePromise = fetch(url, { signal: this.#activeFetch.signal })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.text();
        });

      this.#cachedRecommendations[url] = responsePromise;
      const data = await responsePromise;
      return { success: true, data };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      return { success: false, status: error.message.includes('HTTP') ? parseInt(error.message.split(' ')[1]) : 500 };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Get the first product ID from the cart
   * @returns {string | undefined}
   */
  get productId() {
    return this.dataset.productId;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleRecommendationsError(error) {
    console.error('Cart drawer recommendations error:', error);
    const recommendationsElement = this.querySelector('.drawer__recommendations');
    if (recommendationsElement) {
      /** @type {HTMLElement} */ (recommendationsElement).hidden = true;
      /** @type {HTMLElement} */ (recommendationsElement).dataset.error = 'Error loading recommendations';
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
